import { MediaImportedArgs } from 'containers/MediaUploadModal/types';
import {
  SearchVideoArgs,
  UploadVideoOptions,
} from 'redux/middleware/api/media-upload-service';
import { md5File } from 'utils/file';
import { getMediaImportedProviderArgs } from 'utils/mediaImport';
import { UploadSocialVideoOptions, UploadVideoToStorageOptions } from './types';

/**
 * Serializable, synchronous dedup key for a local File. Two callers holding
 * the same file — even as distinct File objects — derive the same identifier,
 * so `uploadVideoToStorage` can recognize a concurrent upload through Redux
 * without storing the non-serializable File itself.
 */
export function getFileIdentifier(file: File): string {
  return `${file.name}::${file.size}::${file.lastModified}`;
}

export function createSocialVideoUploadArgs(
  opts: UploadSocialVideoOptions,
  args: MediaImportedArgs,
): UploadVideoOptions {
  const { trimStartMillis, trimEndMillis, language } = opts;

  return {
    trimStart: trimStartMillis,
    trimEnd: trimEndMillis,
    transcribe: true,
    language,
    initiateProcessing: true,
    ...getMediaImportedProviderArgs(args),
  };
}

function getProviderVideoSearchOptions(mediaImportedArgs: MediaImportedArgs) {
  switch (mediaImportedArgs.provider) {
    case 'googleDrive': {
      const { googleDriveFileId } = mediaImportedArgs;
      return { googleDriveFileId };
    }

    case 'youtube': {
      const { youtubeUrl } = mediaImportedArgs;
      return { youtubeUrl };
    }

    case 'zoom': {
      const { zoomMeetingId, zoomRecordingFileId } = mediaImportedArgs;
      return { zoomMeetingId, zoomRecordingFileId };
    }

    default:
      return undefined;
  }
}

async function getFileVideoSearchOptions(src: File | string, md5?: string) {
  if (typeof src === 'string') {
    return { videoUrl: src };
  }

  return { md5: md5 ?? (await md5File(src)) };
}

export async function getVideoSearchOptions(
  opts: UploadVideoToStorageOptions,
  mediaImportedArgs?: MediaImportedArgs,
): Promise<SearchVideoArgs> {
  const { trimStartMillis, trimEndMillis, language } = opts;

  const fileSpecificOptions = mediaImportedArgs
    ? getProviderVideoSearchOptions(mediaImportedArgs)
    : await getFileVideoSearchOptions(opts.src, opts.md5);

  return {
    ...fileSpecificOptions,
    trimStartMillis,
    trimEndMillis,
    language,
    enableTranscription: true,
  } as SearchVideoArgs;
}
